// ===========================================
// HOOK: useImpuestosCliente
// ===========================================
// Hook para cargar y gestionar los impuestos asignados a un cliente

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import {
  impuestosClienteService,
  type ImpuestoClienteInsert,
  type ImpuestoClienteUpdate,
  type ImpuestoClienteConCatalogo,
} from '@/services/impuestosClienteService'
import { useNotificacionesModulo } from '@/core/hooks/useNotificacionesModulo'
import { rutaFichaCliente } from '@/lib/ruta-cliente'
import { clientesKeys } from '@/hooks/useClientes'
import { impuestosPageKeys } from '@/hooks/useImpuestosPage'

// Query keys
export const impuestosClienteKeys = {
  all: ['impuestos-cliente'] as const,
  lists: () => [...impuestosClienteKeys.all, 'list'] as const,
  list: (clienteId: string) => [...impuestosClienteKeys.lists(), clienteId] as const,
  /** Lo que lee la tabla de clientes para todas sus filas de una vez. */
  batches: () => [...impuestosClienteKeys.all, 'batch'] as const,
  batch: (clienteIds: string[]) => [...impuestosClienteKeys.batches(), clienteIds] as const,
}

/** Todo lo que lee impuestos_cliente: la ficha, la tabla de clientes y la página de Impuestos. */
const invalidarImpuestosDeCliente = (queryClient: QueryClient, clienteId: string) => {
  queryClient.invalidateQueries({ queryKey: impuestosClienteKeys.list(clienteId) })
  queryClient.invalidateQueries({ queryKey: impuestosClienteKeys.batches() })
  queryClient.invalidateQueries({ queryKey: clientesKeys.lists() })
  queryClient.invalidateQueries({ queryKey: impuestosPageKeys.all })
}

/**
 * Hook para obtener los impuestos asignados a un cliente
 */
export function useImpuestosCliente(clienteId: string | undefined) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: impuestosClienteKeys.list(clienteId || ''),
    queryFn: () => impuestosClienteService.listarPorCliente(supabase!, clienteId!),
    enabled: !!supabase && !supabaseLoading && !!clienteId,
    staleTime: 1000 * 60 * 2, // 2 minutos
  })
}

/**
 * Hook para asignar un impuesto a un cliente
 */
export function useAsignarImpuesto() {
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { notificar, responsablesCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({
      clienteId,
      input,
    }: {
      clienteId: string
      input: ImpuestoClienteInsert
      clienteNombre?: string
      impuestoNombre?: string
    }) => impuestosClienteService.asignar(supabase!, clienteId, input),
    onSuccess: async (_, { clienteId, clienteNombre, impuestoNombre }) => {
      invalidarImpuestosDeCliente(queryClient, clienteId)
      toast.success('Impuesto asignado correctamente')

      // Notif: impuesto asignado → notificar a responsables del cliente.
      // Migrado del patrón legacy de auto-notif al canónico (M1 cleanup post-review).
      if (!supabase || !clienteNombre || !impuestoNombre) return
      try {
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await responsablesCliente(clienteId)
        if (destinatarios.length === 0) return
        await notificar({
          tipo: 'cliente',
          titulo: 'Impuesto asignado',
          mensaje: `${actor} asignó ${impuestoNombre} a ${clienteNombre}`,
          enlace: rutaFichaCliente(clienteId),
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando impuesto asignado:', err)
      }
    },
    onError: (error) => {
      console.error('Error al asignar impuesto:', error)
      toast.error('Error al asignar impuesto')
    },
  })
}

/**
 * Hook para desasignar un impuesto de un cliente
 */
export function useDesasignarImpuesto() {
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      clienteId,
      impuestoId,
    }: {
      clienteId: string
      impuestoId: string
    }) => impuestosClienteService.desasignar(supabase!, clienteId, impuestoId),
    onSuccess: (_, { clienteId }) => {
      invalidarImpuestosDeCliente(queryClient, clienteId)
      toast.success('Impuesto desasignado correctamente')
    },
    onError: (error) => {
      console.error('Error al desasignar impuesto:', error)
      toast.error('Error al desasignar impuesto')
    },
  })
}

/**
 * Hook para actualizar un impuesto de cliente
 */
export function useActualizarImpuestoCliente() {
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string
      clienteId: string
      input: ImpuestoClienteUpdate
    }) => impuestosClienteService.actualizar(supabase!, id, input),
    onSuccess: (_, { clienteId }) => {
      invalidarImpuestosDeCliente(queryClient, clienteId)
      toast.success('Impuesto actualizado correctamente')
    },
    onError: (error) => {
      console.error('Error al actualizar impuesto:', error)
      toast.error('Error al actualizar impuesto')
    },
  })
}

/**
 * Hook para sincronizar impuestos de un cliente
 * Recibe array de impuesto_ids y sincroniza con la BD
 */
export function useSincronizarImpuestosCliente() {
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { notificar, responsablesCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({
      clienteId,
      impuestoIds,
    }: {
      clienteId: string
      impuestoIds: string[]
      clienteNombre?: string
      cantidadNuevos?: number
    }) => impuestosClienteService.sincronizar(supabase!, clienteId, impuestoIds),
    onSuccess: async (_, { clienteId, clienteNombre, cantidadNuevos }) => {
      invalidarImpuestosDeCliente(queryClient, clienteId)
      toast.success('Impuestos actualizados correctamente')

      // Notif: nuevos impuestos asignados → notificar a responsables del cliente.
      // Migrado del patrón legacy de auto-notif al canónico (M1 cleanup post-review).
      if (!supabase || !cantidadNuevos || cantidadNuevos <= 0 || !clienteNombre) return
      try {
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await responsablesCliente(clienteId)
        if (destinatarios.length === 0) return
        await notificar({
          tipo: 'cliente',
          titulo: 'Impuestos asignados',
          mensaje: `${actor} asignó ${cantidadNuevos} impuesto${cantidadNuevos > 1 ? 's' : ''} a ${clienteNombre}`,
          enlace: rutaFichaCliente(clienteId),
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando sincronización de impuestos:', err)
      }
    },
    onError: (error) => {
      console.error('Error al sincronizar impuestos:', error)
      toast.error('Error al actualizar impuestos')
    },
  })
}

// Re-export types for convenience
export type { ImpuestoClienteConCatalogo, ImpuestoClienteInsert, ImpuestoClienteUpdate }
